// Oike · Mi marca — how a piece is composed and drawn.
//
// layoutPiece() decides every element (texts, photos, panels) for one piece, one
// format and one slide; drawPiece() paints it on a canvas. The preview and the
// PNG export use the same layout at the export size, so what Ale approves is
// what she downloads. The checks (contrast, size at the usual viewing size,
// text that does not fit, photos without authorization or still reserved) come
// from the same layout. Photos are only scaled uniformly and cropped (coverRect):
// never deformed, retouched or generated.
// layoutPiece is pure: it gets a measure(text, font) function (the canvas in the
// browser, an estimate in the tests).
import {
  BRAND_FORMATS, BRAND_TEMPLATES, STYLE_VERSION, schemeOf, fontStack, contrastRatio, isLargeText, shownSize, LEGIBILITY_MIN,
  AA_NORMAL, AA_LARGE, coverRect, photoUsable,
} from './brandConfig.js';
import { layoutSocial } from './brandSocial.js';

const fontOf = (family, size, weight = 400, italic = false) => `${italic ? 'italic ' : ''}${weight} ${Math.round(size)}px ${fontStack(family)}`;

function wrap(measure, text, font, width) {
  const out = [];
  for (const para of String(text || '').split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(''); continue; }
    let line = '';
    for (let word of words) {
      // A word longer than the line is cut (rare: URLs)
      while (measure(word, font) > width && word.length > 1) {
        let cut = word.length - 1;
        while (cut > 1 && measure(word.slice(0, cut), font) > width) cut--;
        if (line) { out.push(line); line = ''; }
        out.push(word.slice(0, cut)); word = word.slice(cut);
      }
      const next = line ? `${line} ${word}` : word;
      if (measure(next, font) <= width) line = next;
      else { if (line) out.push(line); line = word; }
    }
    if (line) out.push(line);
  }
  while (out.length && out[out.length - 1] === '') out.pop();
  return out;
}

/** The biggest size (maxSize → minSize) at which the text fits the box. */
export function fitText(measure, text, { family, weight = 400, maxSize, minSize, w, h, lineHeight = 1.2, italic = false }) {
  let size = maxSize;
  for (;;) {
    const font = fontOf(family, size, weight, italic);
    const lines = wrap(measure, text, font, w);
    if (lines.length * size * lineHeight <= h || size <= minSize) {
      return { size, lines, font, height: lines.length * size * lineHeight, overflow: lines.length * size * lineHeight > h + 0.5 };
    }
    size = Math.max(minSize, size - Math.max(1, Math.round(size * 0.04)));
  }
}

const textOf = (slot) => (typeof slot === 'string' ? slot : String(slot?.text || '')).trim();
const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

/**
 * Every element of one piece in one format.
 * piece: { template, format, content: { scheme, slots, slides, people }, photos: { [slot]: { photoId, crop } | { reserved: true } } }
 * ctx: { brand (profile), measure, images: { [slot]: { w, h, photo } }, when (webinarWhen), slide }
 */
export function layoutPiece(piece, { brand, measure, images = {}, when = null, slide = 0 }) {
  const tpl = BRAND_TEMPLATES[piece.template];
  const fmt = BRAND_FORMATS[piece.format];
  if (!tpl || !fmt || !tpl.formats.includes(piece.format)) return { ops: [], checks: [{ kind: 'format', ok: false, element: 'formato' }], width: 0, height: 0 };
  // The collection "YouTube y LinkedIn" has its own composition (src/brandSocial.js)
  if (tpl.collection) return layoutSocial(piece, { brand, measure, images, slide }, { fitText, fontOf });
  const W = fmt.width, H = fmt.height, m = fmt.margin;
  const pal = brand.palette;
  // v2 (approved style): forest green, ivory and mustard for every piece; v1 pieces keep their scheme
  const v2 = (piece.templateVersion || 1) >= STYLE_VERSION;
  const scheme = schemeOf(piece);
  const color = (role) => pal[scheme[role]];
  const fonts = brand.fonts || { title: 'Georgia', body: 'Arial' };
  const ops = [{ type: 'rect', x: 0, y: 0, w: W, h: H, color: color('bg') }];
  const checks = [];
  const content = piece.content || {};
  const slides = Array.isArray(content.slides) ? content.slides : [];
  const current = tpl.slides ? (slides[Math.min(slide, slides.length - 1)] || {}) : null;
  const slotsNow = current ? (current.slots || {}) : (content.slots || {});
  const slotText = (k) => textOf(slotsNow[k]);
  const signature = textOf(content.slots?.signature ?? content.signature ?? '') || (tpl.defaults?.signature ?? '');

  /** A block of text in a box; returns its height. bg is what is behind it (for the contrast check). */
  const text = (role, value, box, o = {}) => {
    const v = String(value || '').trim();
    if (!v) return 0;
    const family = o.family || (role === 'headline' ? fonts.title : fonts.body);
    const r = fitText(measure, o.upper ? v.toUpperCase() : v, { family, weight: o.weight || 400, maxSize: o.max, minSize: o.min, w: box.w, h: box.h, lineHeight: o.lh || 1.2, italic: o.italic });
    const bg = o.bg || color('bg');
    let fg = o.color || (role === 'headline' ? color('title') : color('body'));
    // An accent-colored text only where it passes for its size; otherwise the title color
    if (o.accent) {
      const accent = color('accent');
      const need = isLargeText(r.size, piece.format, (o.weight || 400) >= 700) ? AA_LARGE : AA_NORMAL;
      fg = contrastRatio(accent, bg) >= need ? accent : (o.color || color('title'));
    }
    const y0 = o.alignBottom ? box.y + box.h - r.height : o.center ? box.y + (box.h - r.height) / 2 : box.y;
    ops.push({ type: 'text', role, element: o.element || role, lines: r.lines, font: r.font, size: r.size, lh: o.lh || 1.2, color: fg, x: box.x, y: y0, w: box.w, align: o.align || 'left' });
    const large = isLargeText(r.size, piece.format, (o.weight || 400) >= 700);
    const ratio = contrastRatio(fg, bg);
    checks.push({ kind: 'contrast', element: o.element || role, fg, bg, ratio, needed: large ? AA_LARGE : AA_NORMAL, ok: ratio >= (large ? AA_LARGE : AA_NORMAL) });
    const shown = shownSize(r.size, piece.format);
    const min = LEGIBILITY_MIN[role === 'headline' ? 'headline' : role === 'small' || role === 'kicker' ? 'small' : 'body'];
    checks.push({ kind: 'legibility', element: o.element || role, shown, min, ok: shown >= min });
    if (r.overflow) checks.push({ kind: 'overflow', element: o.element || role, ok: false });
    return r.height;
  };

  /** The quotation and its attribution (a source or a person): always drawn with the text. */
  const attributionOf = (k) => {
    const s = slotsNow[k];
    const a = s && typeof s === 'object' ? s.attribution : null;
    if (!a || !a.author) return '';
    return `— ${a.author}${a.work ? `, ${a.work}` : ''}${a.locator ? ` (${a.locator})` : ''}`;
  };
  const quoted = (k) => { const s = slotsNow[k]; const v = slotText(k); return s && typeof s === 'object' && s.attribution?.author && v ? `“${v.replace(/^[“"]|[”"]$/g, '')}”` : v; };

  /** A photo place: the photo (cover), a reserved space, or nothing (typographic). */
  const photoLabel = (key) => tpl.photos.find(x => x.key === key)?.label || key;
  const photo = (key, box, o = {}) => {
    const p = piece.photos?.[key];
    const img = images[key];
    if (p?.photoId && img) {
      const r = coverRect({ w: img.w, h: img.h }, box, p.crop);
      ops.push({ type: 'image', slot: key, box, rect: r, circle: !!o.circle });
      const usable = photoUsable(img.photo);
      if (!usable.ok) checks.push({ kind: 'photo', element: photoLabel(key), reason: usable.reason, ok: false });
      return 'photo';
    }
    if (p?.photoId && !img) { checks.push({ kind: 'photo', element: photoLabel(key), reason: 'loading_or_missing', ok: false }); }
    if (p?.reserved || (p?.photoId && !img)) {
      ops.push({ type: 'placeholder', slot: key, box, circle: !!o.circle, label: o.label || 'Espacio reservado para una foto', color: color('title'), bg: color('bg') });
      if (p?.reserved) checks.push({ kind: 'reserved', element: photoLabel(key), ok: false });
      return 'reserved';
    }
    return 'none';
  };
  const has = (key) => !!(piece.photos?.[key]?.photoId || piece.photos?.[key]?.reserved);
  const rule = (x, y, w = 96, h = 8) => ops.push({ type: 'rect', x, y, w, h, color: color('accent') });

  // ── Templates ──

  /** Approved style (v2): wide margins, big editorial titles, discreet mustard details. */
  const sig = (x, y, w) => {
    if (!signature) return;
    ops.push({ type: 'rect', x, y: y + 12, w: 14, h: 14, color: color('accent') });
    text('small', signature, { x: x + 30, y, w: w - 30, h: 40 }, { max: 30, min: 28, weight: 700, element: 'Firma' });
  };
  const edge = (x, y, w, h) => ops.push({ type: 'rect', x, y, w, h, color: color('accent') });

  if (v2 && (piece.template === 'post' || (piece.template === 'carousel' && current?.kind === 'cover'))) {
    const M = Math.round(m * 1.2);
    const withPhoto = has('main');
    const bottom = H - M - (signature ? 64 : 0);
    let y;
    if (withPhoto) {
      // The photo inside the green, never edge to edge: the green frames it
      const ph = Math.round(H * 0.4);
      photo('main', { x: M, y: M, w: W - 2 * M, h: ph });
      y = M + ph + 56;
    } else {
      y = M + 40;
    }
    rule(M, y, withPhoto ? 72 : 120, withPhoto ? 6 : 8); y += withPhoto ? 34 : 48;
    const kicker = piece.template === 'post' ? slotText('kicker') : '';
    if (kicker) y += text('kicker', kicker, { x: M, y, w: W - 2 * M, h: 50 }, { max: 32, min: 28, weight: 700, upper: true, accent: true, element: 'Antetítulo' }) + 22;
    const body = quoted('body');
    const bodyH = body ? (withPhoto ? 140 : 300) : 0;
    const hl = text('headline', slotText('headline'), { x: M, y, w: W - 2 * M, h: Math.max(90, bottom - y - bodyH - (bodyH ? 36 : 0)) }, { max: withPhoto ? 100 : 128, min: 50, lh: 1.1, element: 'Título' });
    y += hl + 36;
    if (body) {
      const b = text('body', body, { x: M, y, w: W - 2 * M, h: Math.max(60, bottom - y - (attributionOf('body') ? 50 : 0)) }, { max: withPhoto ? 36 : 42, min: 30, lh: 1.38, element: 'Texto' });
      if (attributionOf('body')) text('small', attributionOf('body'), { x: M, y: y + b + 12, w: W - 2 * M, h: 50 }, { max: 28, min: 26, element: 'Atribución' });
    }
    sig(M, H - M - 40, W - 2 * M);
  } else if (v2 && piece.template === 'carousel') {
    const M = Math.round(m * 1.2);
    const index = Math.min(slide, slides.length - 1);
    const closing = current?.kind === 'closing';
    text('small', `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`, { x: W - M - 220, y: M, w: 220, h: 40 }, { max: 28, min: 26, weight: 700, align: 'right', accent: true, element: 'Número de lámina' });
    let y = M + 150;
    rule(M, y, 96, 8); y += 52;
    const bottom = H - M - (closing && signature ? 64 : 0);
    const hl = text('headline', slotText('headline'), { x: M, y, w: W - 2 * M, h: closing ? 460 : 380 }, { max: closing ? 112 : 104, min: 52, lh: 1.08, element: 'Título de la lámina' });
    y += hl + 44;
    if (slotText('body')) {
      const b = text('body', quoted('body'), { x: M, y, w: W - 2 * M, h: Math.max(80, bottom - y - (attributionOf('body') ? 60 : 0)) }, { max: 50, min: 32, lh: 1.4, element: 'Texto de la lámina' });
      if (attributionOf('body')) text('small', attributionOf('body'), { x: M, y: y + b + 16, w: W - 2 * M, h: 50 }, { max: 28, min: 26, element: 'Atribución' });
    }
    if (closing) sig(M, H - M - 40, W - 2 * M);
  } else if (v2 && piece.template === 'youtube') {
    const M = Math.round(m * 1.1);
    const withPhoto = has('main');
    const split = Math.round(W * 0.55);
    if (withPhoto) { photo('main', { x: split, y: 0, w: W - split, h: H }); edge(split - 8, 0, 8, H); }
    const textW = withPhoto ? split - 2 * M + 8 : W - 2 * M - 120;
    let y = M;
    if (slotText('kicker')) y += text('kicker', slotText('kicker'), { x: M, y, w: textW, h: 54 }, { max: 46, min: 40, weight: 700, upper: true, accent: true, element: 'Antetítulo' }) + 18;
    rule(M, y, 110, 10); y += 42;
    // Bottom right is covered by the duration: the title ends above it
    text('headline', slotText('headline'), { x: M, y, w: textW, h: H - y - M }, { max: withPhoto ? 112 : 150, min: 72, weight: 700, lh: 1.06, element: 'Título' });
  } else if (v2 && piece.template === 'article') {
    const M = Math.round(m * 1.15);
    const withPhoto = has('main');
    const split = Math.round(W * 0.6);
    if (withPhoto) { photo('main', { x: split, y: 0, w: W - split, h: H }); edge(split - 6, 0, 6, H); }
    const textW = withPhoto ? split - 2 * M + 6 : W - 2 * M - 80;
    let y = M;
    if (slotText('kicker')) y += text('kicker', slotText('kicker'), { x: M, y, w: textW, h: 40 }, { max: 24, min: 22, weight: 700, upper: true, accent: true, element: 'Sección o serie' }) + 14;
    rule(M, y, 80, 6); y += 30;
    const bottom = H - M - (signature ? 46 : 0);
    const bodyH = slotText('body') ? 100 : 0;
    const hl = text('headline', slotText('headline'), { x: M, y, w: textW, h: bottom - y - bodyH - (bodyH ? 18 : 0) }, { max: withPhoto ? 66 : 76, min: 38, lh: 1.1, element: 'Título' });
    y += hl + 18;
    if (slotText('body')) text('body', slotText('body'), { x: M, y, w: textW, h: Math.max(40, bottom - y) }, { max: 30, min: 26, lh: 1.35, element: 'Bajada' });
    if (signature) text('small', signature, { x: M, y: H - M - 32, w: textW, h: 36 }, { max: 24, min: 22, weight: 700, element: 'Firma' });
  } else if (v2 && piece.template === 'webinar') {
    const landscape = piece.format === 'landscape';
    const story = piece.format === 'ig_story';
    const k = landscape ? 1.25 : 1;
    const M = Math.round(m * 1.2);
    const top = story ? 290 : M;
    const bottomLimit = story ? 1560 : H - M;
    const colW = landscape ? Math.round(W * 0.56) - M : W - 2 * M;
    const people = ['host', 'guest1', 'guest2', 'guest3']
      .filter(key => has(key) || content.people?.[key]?.name)
      .map(key => ({ key, name: images[key]?.photo?.personName || content.people?.[key]?.name || (key === 'host' ? brand.name : ''), role: images[key]?.photo?.personRole || content.people?.[key]?.role || '' }));
    let y = top;
    if (slotText('kicker')) y += text('kicker', slotText('kicker'), { x: M, y, w: colW, h: 50 * k }, { max: 32 * k, min: 28 * k, weight: 700, upper: true, accent: true, element: 'Antetítulo' }) + 20;
    rule(M, y, 96, 8); y += 40;
    const hl = text('headline', slotText('headline'), { x: M, y, w: colW, h: landscape ? 340 : story ? 420 : people.length ? 250 : 360 }, { max: landscape ? 108 : story ? 120 : 96, min: 52, lh: 1.08, element: 'Título' });
    y += hl + 26;
    if (slotText('body')) y += text('body', slotText('body'), { x: M, y, w: colW, h: (landscape ? 170 : story ? 170 : 130) }, { max: (story ? 40 : 36) * k, min: 30 * k, lh: 1.32, element: 'Bajada' }) + 30;
    // When: date, time and the zone, always written, framed by a discreet mustard line
    const whenText = when?.line || '';
    const extra = when?.extraLine || '';
    if (whenText) {
      // The frame grows with the text (up to two lines each): the zone is never cut
      const pad = Math.round(24 * k);
      let h = text('body', whenText, { x: M + 30, y: y + pad, w: colW - 60, h: 2 * 34 * k * 1.2 }, { max: 34 * k, min: 30 * k, weight: 700, element: 'Fecha, hora y zona' });
      if (extra) h += 10 + text('small', extra, { x: M + 30, y: y + pad + h + 10, w: colW - 60, h: 2 * 27 * k * 1.25 }, { max: 27 * k, min: 25 * k, lh: 1.25, element: 'Otras zonas horarias' });
      const ph = Math.round(h + 2 * pad);
      ops.push({ type: 'frame', x: M, y, w: colW, h: ph, color: color('accent'), width: 3, radius: 14 });
      y += ph + 32;
    }
    const n = people.length;
    if (n) {
      const ctaSpace = slotText('cta') && !landscape ? 110 : 0;
      const area = landscape ? { x: Math.round(W * 0.6), y: M, w: W - Math.round(W * 0.6) - M, h: H - 2 * M } : { x: M, y, w: W - 2 * M, h: Math.max(200, bottomLimit - y - ctaSpace) };
      const cols = landscape ? Math.min(2, n) : n;
      const rows = Math.ceil(n / cols);
      const cellW = area.w / cols;
      const d = Math.max(120, Math.round(Math.min(cellW - (landscape ? 90 : 40), (area.h / rows) - 120 * k, landscape ? 330 : n > 2 ? 240 : story ? 340 : 300)));
      const rowH = d + 120 * k;
      // Centered in the space left: no empty band between the people and the call to sign up
      const offY = Math.max(0, (area.h - rows * rowH) / 2);
      people.forEach((p, i) => {
        const cx = area.x + cellW * (i % cols) + cellW / 2;
        const cy = area.y + offY + Math.floor(i / cols) * rowH;
        const box = { x: Math.round(cx - d / 2), y: Math.round(cy), w: d, h: d };
        const shown = photo(p.key, box, { circle: true, label: 'Foto reservada' });
        if (shown === 'none') {
          ops.push({ type: 'rect', x: box.x, y: box.y, w: d, h: d, color: color('panel'), radius: d / 2 });
          text('headline', initials(p.name), { x: box.x, y: box.y, w: d, h: d }, { max: Math.round(d * 0.36), min: 30, align: 'center', center: true, color: color('onPanel'), bg: color('panel'), element: `Iniciales de ${p.name || 'una persona'}` });
        }
        ops.push({ type: 'ring', cx, cy: cy + d / 2, r: d / 2 + 6, color: color('accent'), width: 3 });
        text('small', p.name, { x: cx - cellW / 2 + 8, y: cy + d + 18, w: cellW - 16, h: 44 * k }, { max: 30 * k, min: 26 * k, weight: 700, align: 'center', element: `Nombre de ${p.name || 'una persona'}` });
        if (p.role) text('small', p.role, { x: cx - cellW / 2 + 8, y: cy + d + 18 + 44 * k, w: cellW - 16, h: 60 * k }, { max: 26 * k, min: 25 * k, align: 'center', element: `Rol de ${p.name || 'una persona'}` });
      });
    }
    if (slotText('cta')) {
      const ctaY = landscape ? H - M - 70 : bottomLimit - 64;
      rule(M, ctaY - 20, 60, 6);
      text('body', slotText('cta'), { x: M, y: ctaY, w: colW, h: 64 * k }, { max: 34 * k, min: 30 * k, weight: 700, element: 'Llamado a inscribirse' });
    }
  } else if (piece.template === 'post' || (piece.template === 'carousel' && current?.kind === 'cover')) {
    const withPhoto = has('main');
    let y;
    if (withPhoto) {
      const ph = Math.round(H * 0.5);
      photo('main', { x: 0, y: 0, w: W, h: ph });
      rule(m, ph + 56);
      y = ph + 96;
    } else {
      rule(m, 200, 120, 10);
      y = 260;
    }
    const bottom = H - m - (signature ? 70 : 0);
    const kicker = piece.template === 'post' ? slotText('kicker') : '';
    if (kicker) y += text('kicker', kicker, { x: m, y, w: W - 2 * m, h: 50 }, { max: 32, min: 28, weight: 700, upper: true, accent: true, element: 'Antetítulo' }) + 24;
    const bodyH = slotText('body') ? (withPhoto ? 190 : 320) : 0;
    const hl = text('headline', slotText('headline'), { x: m, y, w: W - 2 * m, h: Math.max(80, bottom - y - bodyH - (bodyH ? 32 : 0)) }, { max: withPhoto ? 80 : 110, min: 48, lh: 1.12, element: 'Título' });
    y += hl + 32;
    if (slotText('body')) {
      const b = text('body', quoted('body'), { x: m, y, w: W - 2 * m, h: Math.max(60, bottom - y - (attributionOf('body') ? 50 : 0)) }, { max: 38, min: 30, lh: 1.35, element: 'Texto' });
      y += b + 12;
      if (attributionOf('body')) text('small', attributionOf('body'), { x: m, y, w: W - 2 * m, h: 50 }, { max: 28, min: 26, element: 'Atribución' });
    }
    if (signature) {
      ops.push({ type: 'rect', x: m, y: H - m - 34, w: 14, h: 14, color: color('accent') });
      text('small', signature, { x: m + 30, y: H - m - 46, w: W - 2 * m - 30, h: 40 }, { max: 30, min: 28, weight: 700, element: 'Firma' });
    }
  } else if (piece.template === 'carousel') {
    const index = Math.min(slide, slides.length - 1);
    text('small', `${String(index + 1).padStart(2, '0')}/${String(slides.length).padStart(2, '0')}`, { x: W - m - 200, y: m, w: 200, h: 40 }, { max: 28, min: 26, weight: 700, align: 'right', element: 'Número de lámina' });
    rule(m, 200);
    let y = 250;
    const closing = current?.kind === 'closing';
    const bottom = H - m - (closing && signature ? 80 : 0);
    const hl = text('headline', slotText('headline'), { x: m, y, w: W - 2 * m, h: closing ? 420 : 360 }, { max: 84, min: 50, lh: 1.12, element: 'Título de la lámina' });
    y += hl + 40;
    if (slotText('body')) {
      const b = text('body', quoted('body'), { x: m, y, w: W - 2 * m, h: Math.max(80, bottom - y - (attributionOf('body') ? 60 : 0)) }, { max: 44, min: 32, lh: 1.4, element: 'Texto de la lámina' });
      if (attributionOf('body')) text('small', attributionOf('body'), { x: m, y: y + b + 16, w: W - 2 * m, h: 50 }, { max: 28, min: 26, element: 'Atribución' });
    }
    if (closing && signature) {
      ops.push({ type: 'rect', x: m, y: H - m - 34, w: 14, h: 14, color: color('accent') });
      text('small', signature, { x: m + 30, y: H - m - 46, w: W - 2 * m - 30, h: 40 }, { max: 30, min: 28, weight: 700, element: 'Firma' });
    }
  } else if (piece.template === 'youtube') {
    const withPhoto = has('main');
    const textW = withPhoto ? Math.round(W * 0.56) - m : W - 2 * m;
    if (withPhoto) photo('main', { x: Math.round(W * 0.56), y: 0, w: W - Math.round(W * 0.56), h: H });
    let y = m + 20;
    if (slotText('kicker')) y += text('kicker', slotText('kicker'), { x: m, y, w: textW, h: 50 }, { max: 48, min: 40, weight: 700, upper: true, accent: true, element: 'Antetítulo' }) + 20;
    rule(m, y, 110, 10); y += 40;
    // Bottom right is covered by the duration: the text ends above it
    text('headline', slotText('headline'), { x: m, y, w: textW, h: H - y - m - 20 }, { max: withPhoto ? 104 : 128, min: 72, weight: 700, lh: 1.08, element: 'Título' });
  } else if (piece.template === 'article') {
    const withPhoto = has('main');
    const textW = withPhoto ? Math.round(W * 0.58) - m : W - 2 * m;
    if (withPhoto) photo('main', { x: Math.round(W * 0.6), y: 0, w: W - Math.round(W * 0.6), h: H });
    let y = m;
    if (slotText('kicker')) y += text('kicker', slotText('kicker'), { x: m, y, w: textW, h: 40 }, { max: 24, min: 22, weight: 700, upper: true, accent: true, element: 'Sección o serie' }) + 16;
    rule(m, y, 80, 6); y += 30;
    const bottom = H - m - (signature ? 50 : 0);
    const bodyH = slotText('body') ? 110 : 0;
    const hl = text('headline', slotText('headline'), { x: m, y, w: textW, h: bottom - y - bodyH - (bodyH ? 20 : 0) }, { max: 64, min: 36, lh: 1.12, element: 'Título' });
    y += hl + 20;
    if (slotText('body')) text('body', slotText('body'), { x: m, y, w: textW, h: Math.max(40, bottom - y) }, { max: 30, min: 26, lh: 1.35, element: 'Bajada' });
    if (signature) text('small', signature, { x: m, y: H - m - 34, w: textW, h: 36 }, { max: 24, min: 22, weight: 700, element: 'Firma' });
  } else if (piece.template === 'webinar') {
    const people = ['host', 'guest1', 'guest2', 'guest3']
      .filter(k => has(k) || content.people?.[k]?.name)
      .map(k => ({ key: k, name: images[k]?.photo?.personName || content.people?.[k]?.name || (k === 'host' ? brand.name : ''), role: images[k]?.photo?.personRole || content.people?.[k]?.role || '' }));
    const landscape = piece.format === 'landscape';
    // Landscape is seen smaller (BRAND_FORMATS.viewWidth): its texts are bigger
    const k = landscape ? 1.25 : 1;
    const top = piece.format === 'ig_story' ? 290 : m;
    const bottomLimit = piece.format === 'ig_story' ? 1540 : H - m;
    const colW = landscape ? Math.round(W * 0.55) - m : W - 2 * m;
    let y = top;
    if (slotText('kicker')) y += text('kicker', slotText('kicker'), { x: m, y, w: colW, h: 50 }, { max: 32 * k, min: 28 * k, weight: 700, upper: true, accent: true, element: 'Antetítulo' }) + 20;
    rule(m, y); y += 36;
    const hl = text('headline', slotText('headline'), { x: m, y, w: colW, h: landscape ? 330 : piece.format === 'ig_story' ? 380 : 260 }, { max: landscape ? 100 : 84, min: 52, lh: 1.1, element: 'Título' });
    y += hl + 24;
    if (slotText('body')) y += text('body', slotText('body'), { x: m, y, w: colW, h: landscape ? 170 : 130 }, { max: 36 * k, min: 30 * k, lh: 1.3, element: 'Bajada' }) + 28;
    // When: date, time and the time zone, in a panel (never without the zone)
    const whenText = when?.line || '';
    const extra = when?.extraLine || '';
    const panelH = whenText ? Math.round((extra ? 150 : 104) * k) : 0;
    if (whenText) {
      ops.push({ type: 'rect', x: m, y, w: colW, h: panelH, color: color('panel'), radius: 16 });
      text('body', whenText, { x: m + 28, y: y + 22 * k, w: colW - 56, h: 64 * k }, { max: 34 * k, min: 30 * k, weight: 700, color: color('onPanel'), bg: color('panel'), element: 'Fecha, hora y zona' });
      if (extra) text('small', extra, { x: m + 28, y: y + 90 * k, w: colW - 56, h: 48 * k }, { max: 27 * k, min: 25 * k, color: color('onPanel'), bg: color('panel'), element: 'Otras zonas horarias' });
      y += panelH + 28;
    }
    // People: photos in circles (cropped, never altered) or their initials
    const n = people.length;
    if (n) {
      const area = landscape ? { x: Math.round(W * 0.58), y: m, w: W - Math.round(W * 0.58) - m, h: H - 2 * m } : { x: m, y, w: W - 2 * m, h: Math.max(220, bottomLimit - y - 110) };
      const cols = landscape ? Math.min(2, n) : n;
      const rows = Math.ceil(n / cols);
      const cellW = area.w / cols;
      const d = Math.round(Math.min(cellW - 30, (area.h / rows) - 130 * k, landscape ? 340 : n > 2 ? 230 : 280));
      people.forEach((p, i) => {
        const cx = area.x + cellW * (i % cols) + cellW / 2;
        const cy = area.y + Math.floor(i / cols) * (d + 130 * k);
        const box = { x: Math.round(cx - d / 2), y: Math.round(cy), w: d, h: d };
        const shown = photo(p.key, box, { circle: true, label: 'Foto reservada' });
        if (shown === 'none') {
          ops.push({ type: 'rect', x: box.x, y: box.y, w: d, h: d, color: color('panel'), radius: d / 2 });
          text('headline', initials(p.name), { x: box.x, y: box.y, w: d, h: d }, { max: Math.round(d * 0.38), min: 30, align: 'center', center: true, color: color('onPanel'), bg: color('panel'), element: `Iniciales de ${p.name || 'una persona'}` });
        }
        text('small', p.name, { x: cx - cellW / 2 + 10, y: cy + d + 14, w: cellW - 20, h: 44 * k }, { max: 30 * k, min: 26 * k, weight: 700, align: 'center', element: `Nombre de ${p.name || 'una persona'}` });
        if (p.role) text('small', p.role, { x: cx - cellW / 2 + 10, y: cy + d + 14 + 46 * k, w: cellW - 20, h: 64 * k }, { max: 26 * k, min: 25 * k, align: 'center', element: `Rol de ${p.name || 'una persona'}` });
      });
      if (!landscape) y = area.y + rows * (d + 130 * k);
    }
    if (slotText('cta')) {
      const ctaY = landscape ? H - m - 80 : bottomLimit - 70;
      rule(m, ctaY - 18, 60, 6);
      text('body', slotText('cta'), { x: m, y: ctaY, w: colW, h: 64 * k }, { max: 34 * k, min: 30 * k, weight: 700, element: 'Llamado a inscribirse' });
    }
  }
  return { ops, checks, width: W, height: H };
}

/** Paints a layout. images: { [slot]: { img: CanvasImageSource } }. preview adds nothing: overlays are drawn apart. */
export function drawPiece(ctx, layout, images = {}) {
  ctx.save();
  ctx.textBaseline = 'top';
  for (const op of layout.ops) {
    if (op.type === 'rect') {
      ctx.fillStyle = op.color;
      if (op.radius) { roundRect(ctx, op.x, op.y, op.w, op.h, op.radius); ctx.fill(); } else ctx.fillRect(op.x, op.y, op.w, op.h);
    } else if (op.type === 'frame') {
      ctx.strokeStyle = op.color; ctx.lineWidth = op.width;
      roundRect(ctx, op.x + op.width / 2, op.y + op.width / 2, op.w - op.width, op.h - op.width, op.radius || 0); ctx.stroke();
    } else if (op.type === 'ring') {
      ctx.strokeStyle = op.color; ctx.lineWidth = op.width;
      ctx.beginPath(); ctx.arc(op.cx, op.cy, op.r, 0, Math.PI * 2); ctx.stroke();
    } else if (op.type === 'image') {
      const src = images[op.slot]?.img;
      if (!src) continue;
      ctx.save();
      ctx.beginPath();
      if (op.circle) ctx.arc(op.box.x + op.box.w / 2, op.box.y + op.box.h / 2, op.box.w / 2, 0, Math.PI * 2);
      else ctx.rect(op.box.x, op.box.y, op.box.w, op.box.h);
      ctx.clip();
      ctx.drawImage(src, op.rect.x, op.rect.y, op.rect.w, op.rect.h);
      ctx.restore();
    } else if (op.type === 'placeholder') {
      ctx.save();
      ctx.beginPath();
      if (op.circle) ctx.arc(op.box.x + op.box.w / 2, op.box.y + op.box.h / 2, op.box.w / 2, 0, Math.PI * 2);
      else ctx.rect(op.box.x, op.box.y, op.box.w, op.box.h);
      ctx.clip();
      ctx.fillStyle = op.bg; ctx.fill();
      ctx.strokeStyle = op.color; ctx.globalAlpha = 0.25; ctx.lineWidth = 3;
      for (let i = -op.box.h; i < op.box.w; i += 28) { ctx.beginPath(); ctx.moveTo(op.box.x + i, op.box.y + op.box.h); ctx.lineTo(op.box.x + i + op.box.h, op.box.y); ctx.stroke(); }
      ctx.globalAlpha = 1; ctx.setLineDash([16, 10]); ctx.lineWidth = 4;
      ctx.beginPath();
      if (op.circle) ctx.arc(op.box.x + op.box.w / 2, op.box.y + op.box.h / 2, op.box.w / 2 - 3, 0, Math.PI * 2); else ctx.rect(op.box.x + 3, op.box.y + 3, op.box.w - 6, op.box.h - 6);
      ctx.stroke();
      ctx.setLineDash([]);
      const size = Math.max(18, Math.min(34, Math.round(op.box.w / 12)));
      ctx.font = `700 ${size}px ${fontStack('Arial')}`;
      ctx.fillStyle = op.color; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const label = op.label.toUpperCase();
      const words = label.split(' ');
      const lines = [];
      let line = '';
      for (const w of words) { const next = line ? `${line} ${w}` : w; if (ctx.measureText(next).width > op.box.w * 0.8 && line) { lines.push(line); line = w; } else line = next; }
      lines.push(line);
      lines.forEach((l, i) => ctx.fillText(l, op.box.x + op.box.w / 2, op.box.y + op.box.h / 2 + (i - (lines.length - 1) / 2) * size * 1.25));
      ctx.restore();
      ctx.textBaseline = 'top';
    } else if (op.type === 'text') {
      ctx.font = op.font; ctx.fillStyle = op.color; ctx.textAlign = op.align;
      const x = op.align === 'center' ? op.x + op.w / 2 : op.align === 'right' ? op.x + op.w : op.x;
      op.lines.forEach((l, i) => ctx.fillText(l, x, op.y + i * op.size * op.lh));
    } else if (op.type === 'line') {
      ctx.strokeStyle = op.color; ctx.lineWidth = op.width; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(op.x1, op.y1); ctx.lineTo(op.x2, op.y2); ctx.stroke();
    } else if (op.type === 'rich') {
      // A headline with highlighted words: plain, underlined or in (slightly tilted) blocks
      ctx.font = op.font; ctx.textAlign = 'left';
      for (const l of op.lines) {
        ctx.save();
        ctx.translate(l.x + l.w / 2, l.y + l.h / 2);
        if (l.angle) ctx.rotate((l.angle * Math.PI) / 180);
        for (const r of l.runs) {
          const x0 = r.dx - l.w / 2;
          if (r.bg) { ctx.fillStyle = r.bg; roundRect(ctx, x0, -l.h / 2, r.w, l.h, op.radius ?? op.size * 0.1); ctx.fill(); }
          ctx.fillStyle = r.color;
          ctx.fillText(r.text, x0 + (r.bg ? op.padX : 0), -l.h / 2 + (r.bg ? op.size * 0.15 : 0));
          if (r.underline) { ctx.fillStyle = r.underline; ctx.fillRect(x0, -l.h / 2 + op.size * 1.0, r.tw, Math.max(4, op.size * 0.11)); }
        }
        ctx.restore();
      }
    } else if (op.type === 'person') {
      drawPerson(ctx, op, images[op.slot]?.img);
    } else if (op.type === 'doodle') {
      drawDoodle(ctx, op);
    }
  }
  ctx.restore();
}

/**
 * One person of the collection. A cut-out (transparent background) is drawn as it
 * is; its halo is the silhouette's shape in one color, a few pixels around it,
 * behind the photo. A photo with background is clipped to its place, with the
 * halo as a border. The photo itself is never changed.
 */
function drawPerson(ctx, op, src) {
  if (!src) return;
  const { rect, clip, box, halo } = op;
  if (op.cutout) {
    ctx.save();
    ctx.beginPath(); ctx.rect(clip.x, clip.y, clip.w, clip.h); ctx.clip();
    if (halo) {
      const doc = ctx.canvas.ownerDocument || (typeof document !== 'undefined' ? document : null);
      const pad = Math.ceil(halo.width) + 2;
      const w = Math.ceil(rect.w) + 2 * pad, h = Math.ceil(rect.h) + 2 * pad;
      const mask = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : doc.createElement('canvas');
      mask.width = w; mask.height = h;
      const m = mask.getContext('2d');
      m.drawImage(src, pad, pad, rect.w, rect.h);
      m.globalCompositeOperation = 'source-in';
      m.fillStyle = halo.color; m.fillRect(0, 0, w, h);
      const steps = 24;
      for (let i = 0; i < steps; i++) {
        const a = (i / steps) * Math.PI * 2;
        ctx.drawImage(mask, rect.x - pad + Math.cos(a) * halo.width, rect.y - pad + Math.sin(a) * halo.width);
      }
    }
    ctx.drawImage(src, rect.x, rect.y, rect.w, rect.h);
    ctx.restore();
    return;
  }
  ctx.save();
  roundRect(ctx, box.x, box.y, box.w, box.h, op.radius || 0); ctx.clip();
  ctx.drawImage(src, rect.x, rect.y, rect.w, rect.h);
  ctx.restore();
  if (halo) {
    ctx.save();
    ctx.strokeStyle = halo.color; ctx.lineWidth = halo.width;
    roundRect(ctx, box.x - halo.width / 2, box.y - halo.width / 2, box.w + halo.width, box.h + halo.width, (op.radius || 0) + halo.width / 2); ctx.stroke();
    ctx.restore();
  }
}

/** Small hand-drawn details of the collection, as lines (never over text or faces: the layout checks). */
function drawDoodle(ctx, op) {
  const { x, y, w: s } = op;
  const P = (fx, fy) => [x + fx * s, y + fy * s];
  ctx.save();
  ctx.strokeStyle = op.color; ctx.fillStyle = op.color; ctx.lineWidth = op.width; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  const line = (a, b) => { ctx.beginPath(); ctx.moveTo(...P(...a)); ctx.lineTo(...P(...b)); ctx.stroke(); };
  if (op.kind === 'bulb') {
    // The bulb, open at the neck
    ctx.beginPath(); ctx.arc(x + 0.5 * s, y + 0.44 * s, 0.19 * s, Math.PI * 0.72, Math.PI * 2.28); ctx.stroke();
    line([0.4, 0.6], [0.42, 0.7]); line([0.6, 0.6], [0.58, 0.7]);
    line([0.41, 0.72], [0.59, 0.72]); line([0.42, 0.77], [0.58, 0.77]); line([0.45, 0.82], [0.55, 0.82]);
    // Filament
    ctx.beginPath(); ctx.moveTo(...P(0.44, 0.6)); ctx.lineTo(...P(0.46, 0.45)); ctx.quadraticCurveTo(...P(0.5, 0.38), ...P(0.54, 0.45)); ctx.lineTo(...P(0.56, 0.6)); ctx.stroke();
    // Rays
    for (const a of [-170, -140, -110, -90, -70, -40, -10]) {
      const r = (a * Math.PI) / 180;
      line([0.5 + Math.cos(r) * 0.27, 0.44 + Math.sin(r) * 0.27], [0.5 + Math.cos(r) * 0.4, 0.44 + Math.sin(r) * 0.4]);
    }
  } else if (op.kind === 'cloud') {
    // A thought cloud: bumps around an oval, and a small tail
    const cx = x + 0.5 * s, cy = y + 0.42 * s, rx = 0.36 * s, ry = 0.22 * s;
    const bumps = 9;
    ctx.beginPath();
    for (let i = 0; i <= bumps; i++) {
      const a0 = (i / bumps) * Math.PI * 2, a1 = ((i + 1) / bumps) * Math.PI * 2, am = (a0 + a1) / 2;
      const p0 = [cx + Math.cos(a0) * rx, cy + Math.sin(a0) * ry], p1 = [cx + Math.cos(a1) * rx, cy + Math.sin(a1) * ry];
      const pc = [cx + Math.cos(am) * rx * 1.32, cy + Math.sin(am) * ry * 1.45];
      if (i === 0) ctx.moveTo(...p0);
      if (i < bumps) ctx.quadraticCurveTo(...pc, ...p1);
    }
    ctx.stroke();
    ctx.beginPath(); ctx.moveTo(...P(0.3, 0.66)); ctx.lineTo(...P(0.22, 0.84)); ctx.lineTo(...P(0.4, 0.7)); ctx.stroke();
  } else if (op.kind === 'sparkle') {
    for (const [a, b] of [[[0.5, 0.1], [0.5, 0.9]], [[0.1, 0.5], [0.9, 0.5]], [[0.25, 0.25], [0.38, 0.38]], [[0.75, 0.25], [0.62, 0.38]], [[0.25, 0.75], [0.38, 0.62]], [[0.75, 0.75], [0.62, 0.62]]]) line(a, b);
  } else if (op.kind === 'arrow') {
    ctx.beginPath(); ctx.moveTo(...P(0.08, 0.25)); ctx.quadraticCurveTo(...P(0.55, 0.05), ...P(0.85, 0.7)); ctx.stroke();
    line([0.85, 0.7], [0.62, 0.62]); line([0.85, 0.7], [0.9, 0.46]);
  }
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr); ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr); ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** The safe-zone overlay (preview only, never exported): what the channel covers, the text margin, where faces go. */
export function drawOverlay(ctx, layout, format) {
  const fmt = BRAND_FORMATS[format];
  ctx.save();
  ctx.lineWidth = 4;
  for (const c of fmt.cover) {
    ctx.fillStyle = 'rgba(180,54,44,0.28)'; ctx.fillRect(c.x, c.y, c.w, c.h);
    ctx.strokeStyle = 'rgba(180,54,44,0.9)'; ctx.strokeRect(c.x + 2, c.y + 2, c.w - 4, c.h - 4);
  }
  ctx.setLineDash([18, 12]); ctx.strokeStyle = 'rgba(45,95,153,0.95)';
  ctx.strokeRect(fmt.margin, fmt.margin, fmt.width - 2 * fmt.margin, fmt.height - 2 * fmt.margin);
  ctx.setLineDash([]);
  // Faces: the upper-middle part of each photo place, away from the edges
  // The collection marks the faces it protects (no text over them)
  for (const f of layout.faces || []) {
    ctx.strokeStyle = 'rgba(30,123,79,0.95)'; ctx.setLineDash([10, 8]);
    ctx.beginPath(); ctx.ellipse(f.x + f.w / 2, f.y + f.h / 2, f.w / 2, f.h / 2, 0, 0, Math.PI * 2); ctx.stroke();
  }
  for (const op of layout.ops) {
    if (layout.faces || (op.type !== 'image' && op.type !== 'placeholder')) continue;
    const b = op.box;
    const fx = b.x + b.w * 0.2, fy = b.y + b.h * (op.circle ? 0.15 : 0.12), fw = b.w * 0.6, fh = b.h * (op.circle ? 0.6 : 0.5);
    ctx.strokeStyle = 'rgba(30,123,79,0.95)'; ctx.setLineDash([10, 8]);
    ctx.beginPath(); ctx.ellipse(fx + fw / 2, fy + fh / 2, fw / 2, fh / 2, 0, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.restore();
}

/** A plain description of the piece for screen readers (the canvas itself is an image). */
export function describeLayout(layout) {
  const texts = layout.ops.filter(o => (o.type === 'text' && !o.decor) || o.type === 'rich').map(o => (o.type === 'rich' ? o.lines.map(l => l.runs.map(r => r.text).join(' ')) : o.lines).join(' ')).filter(Boolean);
  const photos = layout.ops.filter(o => o.type === 'image' || o.type === 'person').length;
  const reserved = layout.ops.filter(o => o.type === 'placeholder').length;
  return [texts.join('. '), photos ? `${photos} foto${photos > 1 ? 's' : ''}` : '', reserved ? `${reserved} espacio${reserved > 1 ? 's' : ''} reservado${reserved > 1 ? 's' : ''} para foto` : ''].filter(Boolean).join('. ');
}

/** What blocks approval and export, in words (the checks come from the layout). */
export function blockingIssues(checks) {
  const out = [];
  for (const c of checks) {
    if (c.ok) continue;
    if (c.kind === 'contrast') out.push(`${c.element}: contraste ${c.ratio}:1 (hace falta ${c.needed}:1).`);
    else if (c.kind === 'legibility') out.push(`${c.element}: en pantalla se vería de ${c.shown} px (mínimo ${c.min} px). Acorta el texto.`);
    else if (c.kind === 'overflow') out.push(`${c.element}: el texto no entra. Acórtalo.`);
    else if (c.kind === 'reserved') out.push(`Hay un espacio reservado sin foto (${c.element}): elige una foto o quita el espacio para usar solo tipografía.`);
    else if (c.kind === 'photo') out.push({ not_authorized: `La foto de ${c.element} no tiene autorización de uso.`, no_name: `Falta el nombre de la persona de la foto (${c.element}).`, no_credit: `Falta el crédito de la foto (${c.element}).`, missing: `La foto de ${c.element} ya no está disponible.`, loading_or_missing: `La foto de ${c.element} no se pudo cargar.` }[c.reason] || `Revisá la foto de ${c.element}.`);
    else if (c.kind === 'format') out.push('El formato no corresponde a la plantilla.');
    else if (c.kind === 'photo_needed') out.push(`Falta la foto de ${c.element}: cargala o cambiá quién aparece.`);
    else if (c.kind === 'photo_small') out.push(`La foto de ${c.element} se vería muy chica (${c.shown} px en el celular): mostrá menos personas o usá el formato vertical.`);
    else if (c.kind === 'face_covered') out.push(`Un texto tapa el rostro de ${c.element}: acortá el texto, ocultá la etiqueta o cambiá el lado de las fotos.`);
    else if (c.kind === 'face_cut') out.push(`El rostro de ${c.element} queda cortado por el borde: movelo hacia el centro.`);
  }
  return [...new Set(out)];
}
